import { useState } from 'react';
import { customPayRunGateway } from '../index';
import type { StepProps } from './CustomPayRunWizard';
import { PayrollRecordsTable } from './PayrollRecordsTable';
import { DiscardRunDialog } from './DiscardRunDialog';
import { DuplicateAckDialog } from './DuplicateAckDialog';
import { ExportCsvButton } from './ExportCsvButton';
import { CPR_PERMISSIONS, NO_PERMISSION_NOTE } from '../permissions';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { RUN_DISCARDED, RUN_WITHDRAWN, type DuplicateAcknowledgementInput, type DuplicateFlag } from '../types';
import { RunSummary } from './RunSummary';
import { usePayrollRecords } from './usePayrollRecords';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';

const HANDED_OFF = 'HandedOffToPayrollReview';
const PENDING_APPROVAL = 'PendingApproval';

/** Step 2 — Review Payroll. The calculated result per employee (existing calculate + exceptions): an employee whose
 * calculation raised an exception shows "Can't Process". "Send for Approval" is the existing hand-off to Payroll Review
 * (acknowledge-and-proceed, then submit-for-review); an exception must be acknowledged first, exactly as the server requires.
 *
 * "Approve Payroll" is offered ONLY to another user who can approve (`approverView`: not the run's creator or submitter),
 * and only once the run is pending approval in Payroll Review. Payroll Review forbids the person who prepared, submitted or
 * ran a calculation in a run from approving it, so the preparer never sees it. For that user it opens the approval step, where
 * the payroll register is loaded for review and Payroll Review's own run-level approve (with its server-side checks) happens. */
export function ReviewPayrollStep({ run, approverView, refreshRun, goToStep }: StepProps) {
  const can = usePermissionCheck();
  const canWrite = can(CPR_PERMISSIONS.write);
  const canApproveRun = can(CPR_PERMISSIONS.approveRun);
  const handedOff = run.status === HANDED_OFF;
  const discarded = run.status === RUN_DISCARDED;
  const withdrawn = run.status === RUN_WITHDRAWN;
  const { loading, error, preview, records, reload } = usePayrollRecords(run.id, true);
  const [search, setSearch] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [duplicateFlags, setDuplicateFlags] = useState<DuplicateFlag[] | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);

  // An employee whose calculation raised an exception can be acknowledged past (they are simply not paid in this run). An employee with NO usable pay
  // method cannot: they would be paid, so they must be fixed or removed first (the server enforces this too).
  const payMethodBlocked = records.filter((r) => r.payMethodBlocked);
  const cannotProcess = records.filter((r) => !r.canProcess && !r.payMethodBlocked);
  const processable = records.length - cannotProcess.length - payMethodBlocked.length;

  let blockedReason: string | null = null;
  if (handedOff || withdrawn) blockedReason = withdrawn ? 'This run was withdrawn after it was sent for approval.' : 'This run has already been sent for approval.';
  else if (discarded) blockedReason = 'This run was discarded and can no longer be sent.';
  else if (!canWrite) blockedReason = NO_PERMISSION_NOTE;
  else if (loading || !preview) blockedReason = 'Send for Approval becomes available once the calculated payroll has loaded.';
  else if (payMethodBlocked.length > 0)
    blockedReason = `${payMethodBlocked.length} ${payMethodBlocked.length === 1 ? 'employee has' : 'employees have'} no usable pay method. Fix it in the employee profile and refresh, or remove them from this run.`;
  else if (processable === 0) blockedReason = "No employee in this run can be processed, so there is nothing to send for approval. Go back to Custom Payroll and fix the problems shown.";
  else if (cannotProcess.length > 0 && !acknowledged) blockedReason = "Acknowledge the employees who can't be processed to continue.";

  /** Sends the run: acknowledge-and-proceed (when not already ReadyForReview), then submit-for-review. `acks` carry the reasons for duplicates. */
  async function send(acks: DuplicateAcknowledgementInput[]) {
    setSending(true);
    setSendError(null);
    try {
      // The server only accepts the hand-off from ReadyForReview, and only moves there once any exception is acknowledged.
      if (run.status !== 'ReadyForReview') await customPayRunGateway.acknowledgeAndProceed(run.id, acknowledged, acks);
      await customPayRunGateway.submitForReview(run.id, acks);
      setDuplicateFlags(null);
      await refreshRun();
      goToStep('pending-payroll');
    } catch (err) {
      // The server re-detects duplicates; if one appeared since the check, show the acknowledgement dialog instead of a dead-end error.
      if (err instanceof ApiError && err.status === 400 && err.message.includes('DUPLICATE_ACKNOWLEDGEMENT_REQUIRED')) {
        try {
          const flags = await customPayRunGateway.checkDuplicates(run.id, records.filter((r) => r.canProcess).map((r) => r.employeeId));
          if (flags.length > 0) setDuplicateFlags(flags);
        } catch {
          /* fall through to the message */
        }
      }
      setSendError(friendlyErrorMessage(err, 'Could not send this run for approval.'));
      await refreshRun();
    } finally {
      setSending(false);
    }
  }

  async function handleSend() {
    if (blockedReason) return;
    setSending(true);
    setSendError(null);
    try {
      const flags = await customPayRunGateway.checkDuplicates(run.id, records.filter((r) => r.canProcess).map((r) => r.employeeId));
      if (flags.length > 0) {
        setDuplicateFlags(flags);
        setSending(false);
        return;
      }
    } catch (err) {
      // The server enforces the rule either way; if the check itself failed, say so rather than skipping it silently.
      setSendError(friendlyErrorMessage(err, 'Could not check whether these employees are already in another run.'));
      setSending(false);
      return;
    }
    await send([]);
  }

  async function handleDiscard(reason: string) {
    setDiscarding(true);
    setDiscardError(null);
    try {
      await customPayRunGateway.discard(run.id, reason);
      setDiscardOpen(false);
      await refreshRun();
    } catch (err) {
      setDiscardError(friendlyErrorMessage(err, 'Could not discard this run.'));
    } finally {
      setDiscarding(false);
    }
  }

  return (
    <div>
      <RunSummary run={run}>
        <div className="cpr-summary__tools">
          <input
            className="search-box"
            type="search"
            placeholder="Search in Table"
            aria-label="Search in table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ExportCsvButton runId={run.id} />
        </div>
      </RunSummary>

      {withdrawn && (
        <p className="cpr-notice cpr-notice--warning" role="status">
          This run was withdrawn by the preparer before it was approved. It is kept in the run history and can no longer be edited or sent.
        </p>
      )}
      {discarded && (
        <p className="cpr-notice cpr-notice--warning" role="status">
          This run was discarded. It is kept in the run history and can no longer be edited or sent.
        </p>
      )}
      {preview && <p className="badge badge--warning">{preview.shadowCalculationWarning}</p>}
      {loading && <p className="muted">Loading…</p>}
      {error && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && (
        <PayrollRecordsTable
          records={records}
          search={search}
          showSelection
          emptyMessage="No employees were calculated for this run."
          onResetSearch={() => setSearch('')}
        />
      )}

      {payMethodBlocked.length > 0 && !handedOff && !discarded && !withdrawn && (
        <div className="cpr-notice cpr-notice--error" role="alert" data-testid="pay-method-blocked">
          <strong>
            {payMethodBlocked.length} {payMethodBlocked.length === 1 ? 'employee has' : 'employees have'} no usable pay method, so this run cannot be sent for approval:
          </strong>
          <ul>
            {payMethodBlocked.map((r) => (
              <li key={r.employeeId}>
                {r.name} — {r.line.payMethodMessage ?? 'no usable pay method'}{' '}
                <a href={`/people/${r.employeeId}/employee-setup?section=payment`} target="_blank" rel="noopener noreferrer">
                  Open their profile to fix it (new tab)
                </a>
              </li>
            ))}
          </ul>
          <p>
            Pay is never defaulted to a method. Fix the pay method in a new tab, then reload this page, or remove the employee from this run in Custom Payroll. This cannot be
            acknowledged past.
          </p>
          <button type="button" className="cpr-btn cpr-btn--outline" onClick={() => void reload()}>
            Check again
          </button>
        </div>
      )}

      {cannotProcess.length > 0 && !handedOff && !discarded && !withdrawn && (
        <div className="cpr-notice cpr-notice--warning" role="alert">
          <strong>
            {cannotProcess.length} {cannotProcess.length === 1 ? 'employee' : 'employees'} can&apos;t be processed:
          </strong>
          <ul>
            {cannotProcess.map((r) => (
              <li key={r.employeeId}>
                {r.name} — {r.line.exceptionFlags}
              </li>
            ))}
          </ul>
          <p>They will not be paid in this run. Open View on a row for details, or go back to Custom Payroll to fix the cause and recalculate.</p>
          <label className="cpr-ack">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
            <span>I have reviewed these exceptions and want to proceed anyway.</span>
          </label>
        </div>
      )}

      {sendError && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {sendError}
        </p>
      )}
      {blockedReason && !handedOff && !loading && (
        <p id="cpr-send-help" className="muted">
          {blockedReason}
        </p>
      )}
      <p className="muted">
        Approval happens on the next step, by an authorized user other than the person who prepared or sent this run.
      </p>

      <div className="cpr-footer">
        <button type="button" className="cpr-btn cpr-btn--outline" onClick={() => goToStep('custom-payroll')}>
          Back to Custom Payroll
        </button>
        <div className="cpr-footer__group">
          {!handedOff && !discarded && !withdrawn && (
            <button
              type="button"
              className="cpr-btn cpr-btn--ghost"
              disabled={!canWrite || sending}
              title={canWrite ? 'Discard this run. It stays in the history, marked Discarded.' : NO_PERMISSION_NOTE}
              onClick={() => {
                setDiscardError(null);
                setDiscardOpen(true);
              }}
            >
              Discard
            </button>
          )}
          {handedOff && approverView && canApproveRun && run.payrollRunStatus === PENDING_APPROVAL ? (
            <button type="button" className="cpr-btn cpr-btn--green" onClick={() => goToStep('pending-payroll')}>
              Approve Payroll
            </button>
          ) : handedOff ? (
            <button type="button" className="cpr-btn cpr-btn--orange" onClick={() => goToStep('pending-payroll')}>
              Go to Pending Payroll
            </button>
          ) : discarded || withdrawn ? null : (
            <button
              type="button"
              className="cpr-btn cpr-btn--orange"
              disabled={sending || blockedReason !== null}
              aria-describedby={blockedReason ? 'cpr-send-help' : undefined}
              onClick={() => void handleSend()}
            >
              {sending ? 'Sending…' : 'Send for Approval'}
            </button>
          )}
        </div>
      </div>

      {duplicateFlags && (
        <DuplicateAckDialog
          flags={duplicateFlags}
          busy={sending}
          error={sendError}
          onConfirm={(acks) => void send(acks)}
          onCancel={() => setDuplicateFlags(null)}
        />
      )}
      {discardOpen && <DiscardRunDialog busy={discarding} error={discardError} onConfirm={(reason) => void handleDiscard(reason)} onCancel={() => setDiscardOpen(false)} />}
    </div>
  );
}
